<script>
import { mapGetters } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'dashboard/composables/useI18n';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import { FLOW_CREATE_PERMISSIONS } from 'dashboard/routes/dashboard/settings/automationFlows/automationFlows.routes';
import FlowItem from './FlowItem.vue';
import PanelEmptyState from '../sidebar/PanelEmptyState.vue';

// Folga entre o topo da coluna e o titulo da secao quando ela e trazida de
// volta: encostado na borda o titulo parece cortado.
const REVEAL_OFFSET = 8;

// Pausa de digitacao que dispara a busca. Mesmo valor da tela de Fluxos em
// Configuracoes (`settings/automationFlows/Index.vue`), que tem a mesma caixa
// de busca -- duas barras iguais reagindo em tempos diferentes leem como lag de
// uma delas.
const INTERVALO_DE_BUSCA = 400;

export default {
  components: {
    FlowItem,
    PanelEmptyState,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { accountScopedUrl } = useAccount();
    const { t } = useI18n();

    return {
      accountScopedUrl,
      t,
    };
  },
  data() {
    return {
      searchKey: '',
      sortOrder: 'asc',
      // Nao reativos por natureza -- so existem para o debounce e para
      // descartar resposta atrasada --, mas ficam aqui para morrerem junto com
      // a instancia do componente.
      searchTimer: null,
      buscaEmitida: 0,
      buscaResolvida: 0,
      termoBuscado: null,
      isSearchFocused: false,
      pageSize: 10,
      // Só exibe a lista após UM fetch bem-sucedido feito POR ESTA aba — o
      // store é compartilhado com a tela de configurações, e dados stale de
      // lá (sem os filtros manual+published) não podem vazar aqui.
      flowsLoaded: false,
    };
  },
  computed: {
    ...mapGetters({
      flows: 'automationFlows/getAutomationFlows',
      flowsUiFlags: 'automationFlows/getUIFlags',
      flowsMeta: 'automationFlows/getMeta',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    // Criar sempre pelo editor de Fluxos: 'automations/macros/new' saiu com as
    // telas legadas, e o fallback por flag saiu com a #728 (o motor deixou de
    // ser gateado, entao a conta com o bit zerado caia numa rota inexistente).
    newFlowUrl() {
      return this.accountScopedUrl('automations/flows/new');
    },
    // Fluxos manuais publicados (motor novo) — única fonte da aba desde que a
    // macro legada saiu.
    manualFlows() {
      if (!this.flowsLoaded) return [];
      return this.flows.filter(
        flow => flow.trigger_type === 'manual' && flow.published_version_id
      );
    },
    isFetchingAny() {
      return this.flowsUiFlags.isFetching;
    },
    totalPages() {
      return Math.ceil(this.flowsMeta.count / this.pageSize);
    },
    // Os quatro passos da barra, na ordem em que aparecem. Em dado, e nao em
    // quatro blocos de template quase iguais: o que muda entre eles e o icone,
    // o destino e quando ficam apagados.
    pageSteps() {
      const current = Number(this.flowsMeta.current_page) || 1;
      const last = this.totalPages;
      // Com os cartoes ainda na tela durante a busca, as setas continuam
      // clicaveis -- e dois cliques seguidos enfileirariam duas paginas, com a
      // ultima resposta a chegar ganhando. Travadas enquanto busca, nao.
      const first = current <= 1 || this.isFetchingAny;
      const final = current >= last || this.isFetchingAny;

      return [
        {
          icon: 'chevrons-left-outline',
          page: 1,
          disabled: first,
          label: this.t('CONVERSATION.FLOWS.LIST.FIRST_PAGE'),
        },
        {
          icon: 'chevron-left-single-outline',
          page: current - 1,
          disabled: first,
          label: this.t('CONVERSATION.FLOWS.LIST.PREVIOUS_PAGE'),
        },
        {
          icon: 'chevron-right-single-outline',
          page: current + 1,
          disabled: final,
          label: this.t('CONVERSATION.FLOWS.LIST.NEXT_PAGE'),
        },
        {
          icon: 'chevrons-right-outline',
          page: last,
          disabled: final,
          label: this.t('CONVERSATION.FLOWS.LIST.LAST_PAGE'),
        },
      ];
    },
    sortTooltip() {
      return this.sortOrder === 'asc'
        ? this.$t('SORT_ORDER.ASC')
        : this.$t('SORT_ORDER.DESC');
    },
    newFlowTooltipOptions() {
      return {
        content: this.$t('AUTOMATION_FLOWS.CONVERSATION_LIST.HEADER_BTN_TXT'),
        placement: 'left',
        boundariesElement: 'viewport',
        container: 'body',
      };
    },
    clearSearchTooltipOptions() {
      return {
        content: this.t('CONVERSATION.FLOWS.LIST.CLEAR_SEARCH'),
        // `left`, igual a dos outros dois botoes da faixa. A dica passa por
        // cima da ponta da caixa de busca, e e o comportamento que se quer: as
        // tres dicas saem do mesmo lado, e a que aparece e sempre a do botao
        // sob o mouse.
        placement: 'left',
        boundariesElement: 'viewport',
        container: 'body',
      };
    },
    sortTooltipOptions() {
      return {
        content: this.sortTooltip,
        placement: 'left',
        boundariesElement: 'viewport',
        container: 'body',
      };
    },
    noRecordsMessage() {
      if (this.searchKey) {
        return this.$t('CONVERSATION.TASKS.NO_RESULTS');
      }
      return this.$t('AUTOMATION_FLOWS.CONVERSATION_LIST.404');
    },
    showEmptyState() {
      return !this.isFetchingAny && !this.manualFlows.length;
    },
    firstIndex() {
      return this.pageSize * (this.flowsMeta.current_page - 1) + 1;
    },
    lastIndex() {
      return Math.min(
        this.flowsMeta.count,
        this.pageSize * this.flowsMeta.current_page
      );
    },
    isFooterVisible() {
      return this.flowsMeta.count && !(this.firstIndex > this.flowsMeta.count);
    },
    canManageFlows() {
      const userPermissions = getUserPermissions(
        this.currentUser,
        this.currentAccountId
      );

      return hasPermissions(FLOW_CREATE_PERMISSIONS, userPermissions);
    },
  },
  // Watcher, e nao `@input` no campo: o valor ja chega aqui pelo `v-model`, e
  // um segundo listener no mesmo elemento dependeria da ordem em que o Vue
  // empilha os dois. E o mesmo desenho da tela de Fluxos em Configuracoes.
  watch: {
    searchKey() {
      this.scheduleSearch();
    },
  },
  mounted() {
    this.fetchFlows();
  },
  // Sem isto o timer pendente dispara depois de a aba fechar e chama
  // `fetchFlows` num componente destruido.
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    // Fluxos manuais publicados do motor novo — mesma busca/ordenação de
    // sempre. Falha degrada em silêncio: um 403 (papel sem permissão de
    // automação) ou queda de rede não pode quebrar a aba nem virar unhandled
    // rejection — só deixa a lista vazia (showEmptyState cobre o caso).
    //
    // A guarda de sequencia existe porque o resultado vai para o STORE, e nao
    // para uma variavel daqui: se a resposta de "cob" chegar depois da de
    // "cobranca", ela sobrescreve a lista certa e nao ha o que descartar. Nesse
    // caso -- e so nesse -- a busca corrente e refeita. Refazer sempre pela
    // pagina 1 e proposital: a paginacao do resultado antigo acabou de ser
    // jogada fora junto com ele.
    fetchFlows(page = 1) {
      this.termoBuscado = this.searchKey;
      this.buscaEmitida += 1;
      const pedido = this.buscaEmitida;

      return this.$store
        .dispatch('automationFlows/get', {
          triggerType: 'manual',
          published: true,
          page,
          perPage: this.pageSize,
          searchKey: this.searchKey,
          sortOrder: this.sortOrder,
        })
        .then(() => {
          this.flowsLoaded = true;
          const chegouAtrasada = pedido < this.buscaResolvida;
          this.buscaResolvida = Math.max(this.buscaResolvida, pedido);
          if (chegouAtrasada) this.fetchFlows(1);
        })
        .catch(() => {
          this.flowsLoaded = false;
        });
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      this.fetchFlows(this.flowsMeta.current_page);
    },
    onPageChange(page) {
      this.fetchFlows(page).then(() => {
        this.$nextTick(() => this.revealList());
      });
    },
    // O painel inteiro e uma coluna rolavel; achar qual ancestral rola e o que
    // permite mexer no scroll sem o componente saber onde foi montado.
    scrollParent(el) {
      let node = el.parentElement;
      while (node) {
        const { overflowY } = window.getComputedStyle(node);
        if (
          /(auto|scroll|overlay)/.test(overflowY) &&
          node.scrollHeight > node.clientHeight
        ) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    },
    // Trocar de pagina muda a ALTURA da secao: voltar de uma pagina de 5
    // cartoes para uma de 10 faz a lista crescer para baixo e passar da borda
    // do painel -- some justamente o rodape com as setas que a pessoa acabou de
    // clicar. Aqui a secao volta para o topo da coluna, e so quando ela nao
    // cabe inteira na janela: se ja esta toda a vista, rolar seria mexer na
    // tela sem motivo.
    revealList() {
      const secao = this.$el.closest('section') || this.$el;
      const coluna = this.scrollParent(secao);
      if (!coluna) return;

      const s = secao.getBoundingClientRect();
      const c = coluna.getBoundingClientRect();
      if (s.top >= c.top && s.bottom <= c.bottom) return;

      const reduzMovimento = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      coluna.scrollTo({
        top: coluna.scrollTop + s.top - c.top - REVEAL_OFFSET,
        behavior: reduzMovimento ? 'auto' : 'smooth',
      });
    },
    // A busca sai sozinha na pausa da digitacao -- nao ha Enter para apertar.
    // Exigir a tecla economizava requisicao, mas a caixa nao anuncia isso em
    // lugar nenhum: quem digitava e esperava ficava olhando a lista inteira,
    // achando que nao havia resultado.
    //
    // O timer e reiniciado a cada tecla, entao escrever "cobranca" de um folego
    // continua rendendo UMA busca; as intermediarias nem chegam a sair. A
    // pagina volta para 1 porque o resultado e outro conjunto.
    scheduleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        // Digitar uma letra e apagar dentro da pausa devolve o campo ao termo
        // que ja esta na tela -- e o botao de limpar chega aqui com a busca do
        // conjunto completo ja feita. Nos dois casos a requisicao seria uma
        // copia da anterior.
        if (this.searchKey === this.termoBuscado) return;
        this.fetchFlows(1);
      }, INTERVALO_DE_BUSCA);
    },
    // Limpar e uma acao explicita: nao faz sentido esperar a pausa de 400ms
    // que existe para nao disparar uma busca por tecla. O timer pendente morre
    // aqui, e a busca do conjunto completo sai na hora.
    clearSearch() {
      clearTimeout(this.searchTimer);
      this.searchKey = '';
      this.fetchFlows(1);
    },
  },
};
</script>

<template>
  <!-- O loading NAO desmonta a busca nem a ordenacao: com o bloco inteiro sob
       v-if, cada troca de pagina e cada busca com debounce destruia e recriava
       o input (foco perdido no meio da digitacao) e fazia o rodape saltar —
       metade do "buga ao navegar" da issue #627. -->
  <div class="flows-list">
    <!-- Cabecalho da secao, medido na referencia da #1243: a busca com a lupa
         DENTRO da caixa, e ao lado dois botoes quadrados de 32px -- criar e
         ordenar.

         `reset-base` e a porta de saida que a propria base oferece: ela tira o
         input inteiro de `input[type='text']:not(.reset-base)`, que traz
         altura, padding, margem, fundo branco, `text-sm` E um anel de foco
         (`ring-2 ring-brand/25`). Empilhar `!` nao resolvia os dois ultimos: o
         `text-xs` perdia por especificidade ((0,1,0) contra (0,2,1)) e o
         `!shadow-none` so zera `--tw-shadow` -- o `--tw-ring-shadow` continuava
         desenhando o contorno azul DENTRO da caixa ao focar. A caixa de fora ja
         responde ao foco com `focus-within:border-brand`. -->
    <div class="flex items-center pb-2">
      <div
        class="flex items-center flex-1 min-w-0 h-8 gap-1.5 px-2 transition-colors duration-150 border rounded-lg bg-surface-sunken dark:bg-surface-raised border-line focus-within:border-brand"
      >
        <fluent-icon icon="search" size="14" class="shrink-0 text-muted" />
        <input
          v-model="searchKey"
          type="text"
          :placeholder="t('CONVERSATION.FLOWS.LIST.SEARCH_PLACEHOLDER')"
          class="flex-1 min-w-0 p-0 m-0 text-xs bg-transparent border-0 rounded-none shadow-none reset-base text-body placeholder:text-subtle focus:outline-none"
          @focus="isSearchFocused = true"
          @blur="isSearchFocused = false"
        />
      </div>
      <!-- Limpar a busca. Fica FORA do involucro que recolhe os outros dois:
           quem tem algo para limpar acabou de digitar, ou seja, o campo esta
           focado -- dentro do involucro o botao sumiria exatamente quando e
           preciso.

           `v-if` no termo, e nao `disabled`: com o campo vazio nao ha o que
           limpar, e botao apagado permanente na barra e ruido.

           `mousedown.prevent` para o campo NAO perder o foco no clique. Sem
           isso o involucro dos vizinhos abre no blur e fecha de novo quando o
           foco volta -- duas animacoes de 200ms para uma acao que nao mexe no
           foco de ninguem. O clique acontece igual, e o Tab continua
           alcancando o botao. -->
      <button
        v-if="searchKey"
        v-tooltip="clearSearchTooltipOptions"
        type="button"
        :aria-label="t('CONVERSATION.FLOWS.LIST.CLEAR_SEARCH')"
        class="flex items-center justify-center w-8 h-8 p-0 ml-1.5 transition-colors duration-150 border rounded-lg shrink-0 bg-surface-sunken dark:bg-surface-raised border-line text-body hover:border-brand hover:text-brand-text"
        @mousedown.prevent
        @click="clearSearch"
      >
        <!-- `view-box` de 16, e nao os 24 do resto do catalogo: o
             `backspace-outline` e da grade de 16 do Fluent -- confira a grade
             antes de trocar o desenho, porque o viewBox errado nao quebra
             nada, so encolhe e descentraliza o icone em silencio.
             E `size` 18, e nao os 16 dos vizinhos: o desenho ocupa 13x10 das
             16 unidades da grade dele, enquanto o `add` e o de ordenar ocupam
             quase toda a grade de 24 -- ao lado deles, a 16 este sairia miudo.
             Medido num render com os tres botoes lado a lado. -->
        <fluent-icon icon="backspace" size="18" view-box="0 0 16 16" />
      </button>
      <!-- Os dois botoes vivem num invólucro proprio para poderem sumir JUNTOS
           enquanto se digita, dando a largura deles ao campo. A animacao e do
           `max-width` (e nao de `width`), porque a largura natural aqui muda com
           a permissao: sem `canManageFlows` sobra so o de ordenar, e um valor
           fixo o encolheria no estado normal. O `ml` tambem entra na transicao
           -- sem isso o espaco entre a caixa e os botoes ficaria para tras.

           `pointer-events-none` no estado recolhido e o par do `overflow-hidden`:
           elemento de 0px de largura ainda recebe clique, e um clique invisivel
           que navega para o editor de fluxos seria pior que nao ter animacao.
           Quem quiser os botoes tira o foco do campo, e eles voltam. -->
      <div
        class="flex items-center gap-1.5 overflow-hidden transition-all duration-200 ease-out shrink-0"
        :class="
          isSearchFocused
            ? 'max-w-0 ml-0 opacity-0 pointer-events-none'
            : 'max-w-[5rem] ml-1.5 opacity-100'
        "
      >
        <router-link
          v-if="canManageFlows"
          v-tooltip="newFlowTooltipOptions"
          :to="newFlowUrl"
          :aria-label="$t('AUTOMATION_FLOWS.CONVERSATION_LIST.HEADER_BTN_TXT')"
          class="flex items-center justify-center w-8 h-8 p-0 transition-colors duration-150 border rounded-lg shrink-0 bg-surface-sunken dark:bg-surface-raised border-line text-body hover:border-brand hover:text-brand-text"
        >
          <fluent-icon icon="add" size="16" />
        </router-link>
        <button
          v-tooltip="sortTooltipOptions"
          type="button"
          :disabled="!manualFlows.length"
          :aria-label="sortTooltip"
          class="flex items-center justify-center w-8 h-8 p-0 transition-colors duration-150 border rounded-lg shrink-0 bg-surface-sunken dark:bg-surface-raised border-line text-body disabled:opacity-40 disabled:cursor-not-allowed enabled:hover:border-brand enabled:hover:text-brand-text"
          @click="toggleSortOrder"
        >
          <!-- O mesmo `sort-icon` do botao de ordenar conversas, na barra de
               busca da lista ao lado. Era um chevron que virava de lado com a
               direcao; a direcao continua dita pela dica, e o icone passa a
               ser reconhecivel como "ordenar" antes de qualquer leitura. -->
          <fluent-icon icon="sort-icon" size="16" />
        </button>
      </div>
    </div>
    <!-- O loading so entra quando nao ha NADA para segurar na tela. Trocar a
         pagina desmontava os dez cartoes e punha um spinner no lugar: a coluna
         encolhia uns 500px, o navegador grampeava o `scrollTop` do painel no
         novo limite, e quando a pagina seguinte chegava o scroll ja tinha
         subido. E a mesma causa do cabecalho, que ja fica montado acima --
         aqui era a camada de baixo. -->
    <woot-loading-state
      v-if="isFetchingAny && !manualFlows.length"
      compact
      :message="t('CONVERSATION.FLOWS.LIST.LOADING')"
    />
    <PanelEmptyState v-else-if="showEmptyState" :message="noRecordsMessage">
      <router-link v-if="!searchKey && canManageFlows" :to="newFlowUrl">
        <woot-button variant="smooth" icon="add" size="tiny">
          {{ $t('AUTOMATION_FLOWS.CONVERSATION_LIST.HEADER_BTN_TXT') }}
        </woot-button>
      </router-link>
    </PanelEmptyState>
    <template v-else>
      <!-- Enquanto a pagina nova nao chega, os cartoes da anterior ficam no
           lugar, apagados e sem clique: a altura da secao nao muda e o scroll
           fica onde estava. -->
      <div
        class="flex flex-col gap-1.5 transition-opacity duration-150"
        :class="isFetchingAny ? 'opacity-50 pointer-events-none' : ''"
      >
        <FlowItem
          v-for="flow in manualFlows"
          :key="`flow-${flow.id}`"
          :flow="flow"
          :conversation-id="conversationId"
        />
      </div>
      <!-- Paginacao propria, e nao a `TableFooterPagination`: aquela e a das
           TABELAS do app (h-8, botoes de 20px, contador no meio) e ocupa duas
           linhas empilhadas numa coluna de 320px. Aqui a referencia pede uma
           linha so -- contagem a esquerda, setas a direita. -->
      <footer
        v-if="isFooterVisible"
        class="flex items-center justify-between gap-2 pt-2"
      >
        <span class="text-xs text-muted">
          {{
            t('CONVERSATION.FLOWS.LIST.RESULTS', {
              firstIndex,
              lastIndex,
              totalCount: flowsMeta.count,
            })
          }}
        </span>
        <!-- As setas aparecem SEMPRE, mesmo com uma pagina so: some-las faz o
             rodape mudar de forma quando a busca filtra a lista, e o lugar dos
             controles passa a depender do resultado. Apagadas ja dizem que nao
             ha para onde ir. -->
        <div class="flex gap-1 shrink-0">
          <button
            v-for="step in pageSteps"
            :key="step.icon"
            type="button"
            :disabled="step.disabled"
            :aria-label="step.label"
            class="flex items-center justify-center w-[1.625rem] h-[1.625rem] p-0 transition-colors duration-150 border rounded-md bg-surface-sunken dark:bg-surface-raised border-line text-subtle disabled:opacity-40 disabled:cursor-not-allowed enabled:hover:text-body enabled:hover:border-line-strong"
            @click="onPageChange(step.page)"
          >
            <!-- `icon-lib="lucide"`, como a paginacao da tela de contatos
                 (`TableFooterPagination.vue`). Os NOMES ja eram os mesmos: o
                 que muda e o modo de desenho -- `fluent` preenche o caminho e
                 `lucide` o traca com 1,67px de espessura. Era so isso que
                 deixava estas setas gordas ao lado das de la. -->
            <fluent-icon :icon="step.icon" size="16" icon-lib="lucide" />
          </button>
        </div>
      </footer>
    </template>
  </div>
</template>

<style scoped lang="scss">
// Sem padding proprio: a `PanelSection` que embrulha esta lista ja da o
// respiro lateral da coluna, e os 4px daqui so recuavam a busca e os cards em
// relacao ao titulo da secao e as demais secoes do painel.
.flows_list--empty-state {
  padding: var(--space-slab);
  p {
    margin: 0;
  }
}
.flows-footer {
  // Custom styles for the footer in this specific component
  // to stack results above pagination.
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-small);
  margin-top: var(--space-small);
}
.flows-footer .text-sm {
  margin-bottom: var(--space-smaller); // Space between text and pagination
}
</style>
